import { useEffect, useRef } from "react";
import { ACTION_SHORTCUTS } from "@compositions/index";

/**
 * Global single-key shortcuts for the work actions.
 *
 *   N  Next        A  Ask        C  New        /  focus search
 *
 * A shortcut fires only when the key press is not already someone else's:
 * never while focus is in an editable control, never while a dialog or a
 * menu is open, never with a modifier held, never after another handler
 * called `preventDefault`. Escape is left to the overlay primitives.
 *
 * The keys themselves live in `ACTION_SHORTCUTS` beside the work-action
 * compositions, so the tooltips and the bindings cannot drift apart.
 */

export interface GlobalShortcutHandlers {
  readonly onNext?: () => void;
  readonly onAsk?: () => void;
  readonly onCreate?: () => void;
  readonly onFocusSearch?: () => void;
}

const EDITABLE_SELECTOR =
  "input, textarea, select, [contenteditable]:not([contenteditable='false'])";
const BLOCKING_OVERLAY_SELECTOR =
  '[role="dialog"], [role="alertdialog"], [aria-modal="true"], [role="menu"]';

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  if (target instanceof HTMLElement && target.isContentEditable) return true;
  return target.closest(EDITABLE_SELECTOR) !== null;
}

function hasBlockingOverlay(root: Document): boolean {
  return root.querySelector(BLOCKING_OVERLAY_SELECTOR) !== null;
}

/**
 * Decide whether a keydown may drive a global shortcut. Exported for the
 * tests and for any surface that wants the same gate.
 */
export function isShortcutEligible(event: KeyboardEvent): boolean {
  if (event.defaultPrevented) return false;
  if (event.ctrlKey || event.metaKey || event.altKey) return false;
  // Shift blocks the letters. `/` is exempt: some layouts reach it only
  // through Shift, and the key value already tells us what was typed.
  if (event.shiftKey && event.key !== ACTION_SHORTCUTS.search) return false;
  if (event.isComposing) return false;
  if (isEditableTarget(event.target)) return false;
  if (hasBlockingOverlay(document)) return false;
  return true;
}

type ShortcutAction = keyof GlobalShortcutHandlers;

function actionForKey(key: string): ShortcutAction | null {
  const upper = key.length === 1 ? key.toUpperCase() : key;
  switch (upper) {
    case ACTION_SHORTCUTS.next:
      return "onNext";
    case ACTION_SHORTCUTS.ask:
      return "onAsk";
    case ACTION_SHORTCUTS.create:
      return "onCreate";
    case ACTION_SHORTCUTS.search:
      return "onFocusSearch";
    default:
      return null;
  }
}

export function useGlobalShortcuts(handlers: GlobalShortcutHandlers, enabled = true): void {
  // Latest-handlers ref: one listener for the life of the shell, no
  // re-subscription when a callback identity changes.
  const handlersRef = useRef(handlers);
  useEffect(() => {
    handlersRef.current = handlers;
  }, [handlers]);

  useEffect(() => {
    if (!enabled) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      const action = actionForKey(event.key);
      if (action === null) return;
      if (!isShortcutEligible(event)) return;
      const handler = handlersRef.current[action];
      if (handler === undefined) return;
      event.preventDefault();
      handler();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [enabled]);
}
